import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom-v5-compat';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { FormPasswordConfirmationModalData } from 'dashboard/forms/FormPasswordConfirmationModal';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Webhook } from 'dashboard/models/webhook';
import { WebhookEvent } from 'dashboard/models/webhook-event';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearWebhook } from 'dashboard/redux/slices/webhook';
import {
  setCurrentPage as setWebhookEventsCurrentPage,
  setFilters as setWebhookEventsFilters,
  setPageSize as setWebhookEventsPageSize,
} from 'dashboard/redux/slices/webhook-events';
import { RootState } from 'dashboard/redux/store';
import {
  archiveWebhook,
  disableWebhook,
  enableWebhook,
  loadWebhook,
  rotateWebhookSecret,
  updateWebhook,
} from 'dashboard/redux/thunk-actions/webhook';
import { rerunWebhookEvent } from 'dashboard/redux/thunk-actions/webhook-event';
import { loadWebhookEventList } from 'dashboard/redux/thunk-actions/webhook-events';
import DashboardWebhooksShowView from 'dashboard/views/DashboardWebhooksShowView';
import { ApiKeyInflection, ApiVersion } from 'lib/constants/api';
import { EventName } from 'lib/constants/events';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useRedirectOnEnvironmentChange from 'lib/hooks/useRedirectOnEnvironmentChange';
import useTable from 'lib/hooks/useTable';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

export interface FormUpdateWebhookData {
  url: string;
  'api-version': ApiVersion;
  'api-key-inflection': ApiKeyInflection;
  'api-attributes-blocklist': string[];
  'included-blocklist': string[];
  'relationship-blocklist': string[];
  'custom-http-headers'?: Record<string, string>;
  'enabled-events'?: EventName[];
  'payload-filter'?: Record<string, unknown>;
  'file-access-token-expires-in'?: number;
}

type Params = { webhookId: string };

const DashboardWebhooksShowRoute = (props: PropsFromRedux) => {
  useOnMountAndUnmount(props.clearWebhook);
  useRedirectOnEnvironmentChange('/dashboard/webhooks');

  const navigate = useNavigate();

  const { webhookId } = useParams<Params>() as Params; // webhookId will always be defined
  const {
    handlePageLinkClick: handleWebhookEventsPageLinkClick,
    handlePageSizeClick: handleWebhookEventsPageSizeClick,
  } = useTable({
    filters: props.webhookEventsFilters,
    pageSize: props.webhookEventsPageSize,
    currentPage: props.webhookEventsCurrentPage,
    setFilters: props.setWebhookEventsFilters,
    setCurrentPage: props.setWebhookEventsCurrentPage,
    setPageSize: props.setWebhookEventsPageSize,
  });

  const { loading: isLoadingWebhook, callback: loadWebhook } = useApiLoader({
    loader: () => props.loadWebhook(webhookId),
    deps: [props.loadWebhook, webhookId],
    onNetworkError: () => props.showMessage('Failed to load webhook.', MessageType.Failure),
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load webhook.', MessageType.Failure);
      }
    },
  });

  const { loading: isLoadingWebhookEvents } = useApiLoader({
    loader: () =>
      props.loadWebhookEventList({
        currentEnvironment: props.currentEnvironment,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentPage: props.webhookEventsCurrentPage,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pageSize: props.webhookEventsPageSize,
        filters: {
          webhookId,
        },
      }),
    deps: [
      props.currentEnvironment,
      props.webhookEventsCurrentPage,
      props.webhookEventsPageSize,
      props.webhookEventsFilters,
      webhookId,
    ],
    onNetworkError: () => {
      props.showMessage('Failed to load webhook events.', MessageType.Failure);
    },
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load webhook events.', MessageType.Failure);
      }
    },
  });

  // ==============================
  // Update Webhook
  // ==============================
  const { error: updateWebhookError, callback: handleUpdateWebhook } = useApiCallback(
    async (
      webhook: Webhook,
      data: FormUpdateWebhookData,
      meta?: FormPasswordConfirmationModalData,
    ) => {
      await props.updateWebhook(
        webhook.id,
        {
          url: data['url'],
          'api-version': data['api-version'],
          'api-key-inflection': data['api-key-inflection'],
          'api-attributes-blocklist': data['api-attributes-blocklist'],
          'included-blocklist': data['included-blocklist'],
          'relationship-blocklist': data['relationship-blocklist'],
          'custom-http-headers': data['custom-http-headers'],
          'enabled-events': data['enabled-events'],
          'payload-filter': data['payload-filter'],
          'file-access-token-expires-in': data['file-access-token-expires-in'],
        },
        {
          // Note this will be blank if organization has SAML enabled.
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'password-confirmation': meta?.['password-confirmation'],
        },
      );

      loadWebhook();
      props.showMessage('Saved webhook', MessageType.Success);
    },
    [props.updateWebhook, props.showMessage, loadWebhook],
  );

  useApiErrorHandler({
    error: updateWebhookError,
    onError: (error) => {
      const details = error.errors?.[0]?.details;
      const errorMessage: string =
        typeof details === 'string' ? details : 'Error encountered when updating webhook.';
      props.showMessage(errorMessage, MessageType.Failure);
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  // ==============================
  // Rotate Webhook Secret
  // ==============================
  const { error: rotateWebhookSecretError, callback: handleRotateWebhookSecret } = useApiCallback(
    async (webhook: Webhook, meta?: FormPasswordConfirmationModalData) => {
      await props.rotateWebhookSecret(webhook.id, {
        // Note this will be blank if organization has SAML enabled.
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'password-confirmation': meta?.['password-confirmation'],
      });

      loadWebhook();
      props.showMessage('Rotated webhook secret', MessageType.Success);
    },
    [props.rotateWebhookSecret, props.showMessage, loadWebhook],
  );

  useApiErrorHandler({
    error: rotateWebhookSecretError,
    onError: () => {
      props.showMessage('Error encountered when rotating webhook secret.', MessageType.Failure);
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  // ==============================
  // Enable Webhook
  // ==============================
  const { error: enableWebhookError, callback: handleEnableWebhook } = useApiCallback(
    async (webhook: Webhook, meta?: FormPasswordConfirmationModalData) => {
      await props.enableWebhook(webhook.id, {
        // Note this will be blank if organization has SAML enabled.
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'password-confirmation': meta?.['password-confirmation'],
      });

      loadWebhook();
      props.showMessage('Enabled webhook', MessageType.Success);
    },
    [props.enableWebhook, props.showMessage, loadWebhook],
  );

  useApiErrorHandler({
    error: enableWebhookError,
    onError: () => {
      props.showMessage('Error encountered when enabling webhook.', MessageType.Failure);
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  // ==============================
  // Disable Webhook
  // ==============================
  const { error: disableWebhookError, callback: handleDisableWebhook } = useApiCallback(
    async (webhook: Webhook, meta?: FormPasswordConfirmationModalData) => {
      await props.disableWebhook(webhook.id, {
        // Note this will be blank if organization has SAML disabled.
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'password-confirmation': meta?.['password-confirmation'],
      });

      loadWebhook();
      props.showMessage('Disabled webhook', MessageType.Success);
    },
    [props.disableWebhook, props.showMessage, loadWebhook],
  );

  useApiErrorHandler({
    error: disableWebhookError,
    onError: () => {
      props.showMessage('Error encountered when disabling webhook.', MessageType.Failure);
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  // ==============================
  // Archive Webhook
  // ==============================
  const { error: archiveWebhookError, callback: handleArchiveWebhook } = useApiCallback(
    async (webhook: Webhook, meta?: FormPasswordConfirmationModalData) => {
      await props.archiveWebhook(webhook.id, {
        // Note this will be blank if organization has SAML archived.
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'password-confirmation': meta?.['password-confirmation'],
      });

      navigate(`/dashboard/webhooks`);
      props.showMessage('Archived webhook', MessageType.Success);
    },
    [props.archiveWebhook, props.showMessage],
  );

  useApiErrorHandler({
    error: archiveWebhookError,
    onError: () => {
      props.showMessage('Error encountered when archiving webhook.', MessageType.Failure);
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  // ==============================
  // Show Webhook Event
  // ==============================
  const handleShowWebhookEvent = (webhookEvent: WebhookEvent) => {
    navigate(`/dashboard/webhook-events/${webhookEvent.id}`);
  };

  // ==============================
  // Render
  // ==============================
  if (props.webhook == null) {
    if (isLoadingWebhook) {
      return <LoadingIndicator fullscreen={true} />;
    } else {
      return (
        <Backdrop>
          <NullState message='We couldn’t find that webhook.' backRoute='/dashboard/webhooks' />
        </Backdrop>
      );
    }
  }

  return (
    <DashboardWebhooksShowView
      organization={props.organization}
      webhook={props.webhook}
      webhookEvents={props.webhookEvents}
      isLoadingWebhookEvents={isLoadingWebhookEvents}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      webhookEventsPageSize={props.webhookEventsPageSize}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      webhookEventsPaginationParams={props.webhookEventsPaginationParams}
      handleUpdateWebhook={handleUpdateWebhook}
      handleRotateWebhookSecret={handleRotateWebhookSecret}
      handleEnableWebhook={handleEnableWebhook}
      handleDisableWebhook={handleDisableWebhook}
      handleArchiveWebhook={handleArchiveWebhook}
      handleShowWebhookEvent={handleShowWebhookEvent}
      handleWebhookEventsPageLinkClick={handleWebhookEventsPageLinkClick}
      handleWebhookEventsPageSizeClick={handleWebhookEventsPageSizeClick}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  organization: state.organization.organization,
  currentEnvironment: state.currentEnvironment.currentEnvironment,
  webhook: state.webhook.webhook,
  webhookEvents: state.webhookEvents.webhookEvents,
  webhookEventsPageSize: state.webhookEvents.pageSize,
  webhookEventsCurrentPage: state.webhookEvents.currentPage,
  webhookEventsPaginationParams: state.webhookEvents.paginationParams,
  webhookEventsFilters: state.webhookEvents.filters,
});

const mapDispatchToProps = {
  loadWebhook,
  clearWebhook,
  updateWebhook,
  rotateWebhookSecret,
  enableWebhook,
  disableWebhook,
  archiveWebhook,
  rerunWebhookEvent,
  loadWebhookEventList,
  setWebhookEventsCurrentPage,
  setWebhookEventsFilters,
  setWebhookEventsPageSize,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardWebhooksShowRoute);
